import React, { useContext, useEffect, useState } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import useExtendedDeviation from '../../../../../utils/hooks/useExtendedDeviation';
import BoostNoCoreModal from '../BoostNoCoreModal';
import BoostUpgradeCoreModal from '../BoostUpgradeCoreModal';
import BoostModal from '../BoostModal';
import BoostSuccessModal from '../BoostSuccessModal';
import AlreadyBoostedModal from '../AlreadyBoostedModal';
import BoostNotSupportedModal from '../BoostNotSupportedModal';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';

interface Props {
  deviation: PapiDeviation;
  onRequestClose: () => void;
  /**
   * When mounted via a modal dispatch, we wont' necessarily have an appropriate sectionname inside the
   * bi context. So we allow passing a sectionname override.
   */
  sectionNameOverride?: string;
  onSuccess?: () => void;
}

type ModalType =
  | 'no-core'
  | 'upgrade-core'
  | 'loading'
  | 'boost'
  | 'success'
  | 'already-boosted'
  | 'not-boostable';

const BoostRootModal: React.FC<Props> = ({
  deviation: deviationFromProps,
  onRequestClose,
  sectionNameOverride,
  onSuccess: onSuccessFromProps,
}) => {
  const [showingModal, setShowingModal] = useState<ModalType>('loading');
  const [wasJustBoosted, setWasJustBoosted] = useState(false);
  const onSuccess = () => {
    setWasJustBoosted(true);
    onSuccessFromProps && onSuccessFromProps();
  };
  const { hasCoreMembership, coreLevel } = useContext(SessionContext);
  const deviation =
    useExtendedDeviation(deviationFromProps, 'isBoostable') ||
    deviationFromProps;

  const isDeviationExtended = deviation.extended?.isBoostable !== undefined;
  const isBoosted = !!deviation.extended?.boosted;
  const isBoostable = !!deviation.extended?.isBoostable;

  useEffect(() => {
    let showModalType: ModalType = 'boost';

    if (!hasCoreMembership) {
      showModalType = 'no-core';
    } else if (coreLevel === 'core_access') {
      showModalType = 'upgrade-core';
    } /* user has the right core */ else {
      // If we just boosted successfully, show success immediately regardless of extended data
      if (wasJustBoosted) {
        showModalType = 'success';
      } else if (!isDeviationExtended) {
        showModalType = 'boost';
      } else {
        if (isBoosted) {
          showModalType = 'already-boosted';
        } else if (!isBoostable) {
          showModalType = 'not-boostable';
        }
      }
    }

    setShowingModal(showModalType);
  }, [
    showingModal,
    isDeviationExtended,
    isBoostable,
    isBoosted,
    wasJustBoosted,
    hasCoreMembership,
    coreLevel,
    deviation.deviationId,
  ]);

  return (
    <BiLoggerContextProvider
      key={deviation.deviationId}
      value={{
        sectionname: sectionNameOverride,
      }}
    >
      <>
        {showingModal === 'loading' && (
          <LoadingIndicator view="llama" usePortal withOverlay />
        )}
        {showingModal === 'no-core' && (
          <BoostNoCoreModal onRequestClose={onRequestClose} />
        )}
        {showingModal === 'upgrade-core' && (
          <BoostUpgradeCoreModal onRequestClose={onRequestClose} />
        )}
        {showingModal === 'boost' && (
          <BoostModal
            deviation={deviation}
            onSuccess={onSuccess}
            onRequestClose={onRequestClose}
          />
        )}
        {showingModal === 'success' && (
          <BoostSuccessModal onRequestClose={onRequestClose} />
        )}
        {showingModal === 'already-boosted' && (
          <AlreadyBoostedModal onRequestClose={onRequestClose} />
        )}
        {showingModal === 'not-boostable' && (
          <BoostNotSupportedModal onRequestClose={onRequestClose} />
        )}
      </>
    </BiLoggerContextProvider>
  );
};
BoostRootModal.displayName = 'BoostRootModal';

export default BoostRootModal;
